/**
 * 组件图鉴 atom 演示的通用绘制辅助与类型定义。
 * 画布统一使用竖屏逻辑尺寸，DPR 缩放在 AtomPreview 层处理。
 */

export const LOGICAL_W = 340;
export const LOGICAL_H = 440;

export const PALETTE = ['#22d3ee', '#f472b6', '#a3e635', '#fbbf24', '#c084fc', '#f87171'];

export function valColor(v: number): string {
  if (v === null || v === undefined) return '#1e293b';
  return PALETTE[(Math.abs(v) - 1) % PALETTE.length];
}

export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function fillRound(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number, color: string,
): void {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fillStyle = color;
  ctx.fill();
}

export function label(
  ctx: CanvasRenderingContext2D,
  text: string, x: number, y: number,
  color = '#cbd5e1', size = 12, align: CanvasTextAlign = 'left',
): void {
  ctx.fillStyle = color;
  ctx.font = `${size}px system-ui, sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y);
}

export interface DemoAction {
  label: string;
  run: () => void;
}

export interface DemoHandle {
  update?: (dt: number) => void;
  render?: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  destroy?: () => void;
  actions?: DemoAction[];
  getState?: () => Record<string, string | number | boolean>;
  hint?: string;
}

export interface DemoEnv {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/** match 类原子要求的 grid 协议：cols/rows/getCell/setCell/isValid/cellToPixel */
export interface StubGrid {
  cols: number;
  rows: number;
  cellSize: number;
  offsetX: number;
  offsetY: number;
  data: number[][];
  getCell(c: number, r: number): number | null;
  setCell(c: number, r: number, v: number | null): void;
  isValid(c: number, r: number): boolean;
  cellToPixel(c: number, r: number): { x: number; y: number };
  forEachCell(cb: (v: number, c: number, r: number) => void): void;
}

export function makeStubGrid(cols = 6, rows = 6, types = 4, cellSize = 44, offsetX = 20, offsetY = 56): StubGrid {
  const data: number[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: number[] = [];
    for (let c = 0; c < cols; c++) row.push(1 + Math.floor(Math.random() * types));
    data.push(row);
  }
  const isValid = (c: number, r: number) => c >= 0 && r >= 0 && c < cols && r < rows;
  return {
    cols, rows, cellSize, offsetX, offsetY, data,
    getCell(c, r) { return isValid(c, r) ? data[r][c] : null; },
    setCell(c, r, v) { if (isValid(c, r)) data[r][c] = v as number; },
    isValid,
    cellToPixel(c, r) { return { x: offsetX + c * cellSize + cellSize / 2, y: offsetY + r * cellSize + cellSize / 2 }; },
    forEachCell(cb) { for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) cb(data[r][c], c, r); },
  };
}

export function drawStubGrid(ctx: CanvasRenderingContext2D, grid: StubGrid, highlight?: Set<string>): void {
  grid.forEachCell((v, c, r) => {
    const p = grid.cellToPixel(c, r);
    const s = grid.cellSize - 4;
    const key = `${c},${r}`;
    fillRound(ctx, p.x - s / 2, p.y - s / 2, s, s, 8, highlight?.has(key) ? '#fbbf24' : valColor(v));
  });
}

/** 程序化生成 8 帧旋转菱形 dataURL（sprite-animation 演示用） */
export function makeSpinFrames(): string[] {
  const frames: string[] = [];
  for (let i = 0; i < 8; i++) {
    const c = document.createElement('canvas');
    c.width = 72; c.height = 72;
    const g = c.getContext('2d');
    if (!g) continue;
    g.translate(36, 36);
    g.rotate((Math.PI * 2 * i) / 8);
    g.fillStyle = '#fbbf24';
    g.beginPath();
    g.moveTo(0, -28); g.lineTo(12, 0); g.lineTo(0, 28); g.lineTo(-12, 0);
    g.closePath(); g.fill();
    g.fillStyle = '#22d3ee';
    g.beginPath(); g.arc(0, 0, 8, 0, Math.PI * 2); g.fill();
    frames.push(c.toDataURL());
  }
  return frames;
}

/** 生成一张纯色宝石 dataURL（image-loader 演示用） */
export function makeGem(color: string, star = false): string {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const g = c.getContext('2d');
  if (!g) return '';
  g.fillStyle = color;
  g.beginPath();
  if (star) {
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? 28 : 12;
      const a = (Math.PI * 2 * i) / 10 - Math.PI / 2;
      const x = 32 + r * Math.cos(a);
      const y = 32 + r * Math.sin(a);
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
  } else {
    g.moveTo(32, 4); g.lineTo(60, 32); g.lineTo(32, 60); g.lineTo(4, 32);
  }
  g.closePath(); g.fill();
  return c.toDataURL();
}

/** 极短静音 wav dataURL（audio-bgm 演示用，避免外网依赖） */
export function silentWavUrl(): string {
  const n = 1600;
  const bytes = new Uint8Array(44 + n);
  const dv = new DataView(bytes.buffer);
  const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i); };
  ws(0, 'RIFF'); dv.setUint32(4, 36 + n, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, 8000, true); dv.setUint32(28, 8000, true);
  dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
  ws(36, 'data'); dv.setUint32(40, n, true);
  for (let i = 0; i < n; i++) bytes[44 + i] = 128;
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return 'data:audio/wav;base64,' + btoa(bin);
}

/** 从 pointer 事件取 canvas 逻辑坐标（CSS 像素） */
export function eventPos(canvas: HTMLCanvasElement, e: PointerEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}
